import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import {
  MessageCircle,
  BookOpen,
  Plus,
  TrendingUp,
  Calendar,
  Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { TopicCard } from "@/components/TopicCard";
import { JournalPreview } from "@/components/JournalPreview";
import type { Topic, DailyJournal } from "@/types";

/**
 * Dashboard page displaying overview of topics and recent journals
 *
 * @returns Dashboard with topic list, recent journals, and quick actions
 */
export function Dashboard() {
  const { getToken } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [recentJournals, setRecentJournals] = useState<DailyJournal[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        if (!token) return;

        const [topicsData, journalsData] = await Promise.all([
          api.getTopics(token),
          api.getRecentJournals(token, 3),
        ]);

        setTopics(topicsData);
        setRecentJournals(journalsData);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getToken]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse-subtle text-primary-600">
          <Sparkles size={48} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-primary-950">
            Welcome back!
          </h1>
          <p className="text-slate-500 mt-1">
            Continue your learning journey today
          </p>
        </div>
        <Link
          to="/chat"
          className="flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-medium shadow-lg shadow-primary-600/25 hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          New Topic
        </Link>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-6 border shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-study-light rounded-xl">
              <MessageCircle className="text-study" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-primary-950">
                {topics.length}
              </p>
              <p className="text-sm text-slate-500">Active Topics</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-life-light rounded-xl">
              <BookOpen className="text-life" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-primary-950">
                {recentJournals.length}
              </p>
              <p className="text-sm text-slate-500">Recent Journals</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-accent-100 rounded-xl">
              <TrendingUp className="text-accent-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-primary-950">
                {topics.filter((t) => t.category === "study").length}
              </p>
              <p className="text-sm text-slate-500">Study Topics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Topics section */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-display font-semibold text-primary-950">
              Your Topics
            </h2>
            <Link
              to="/chat"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all
            </Link>
          </div>

          {topics.length === 0 ? (
            <div className="bg-white rounded-2xl border p-8 text-center">
              <MessageCircle className="mx-auto text-slate-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-slate-700">No topics yet</h3>
              <p className="text-slate-500 mt-1">
                Start a new conversation to create your first topic
              </p>
              <Link
                to="/chat"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                <Plus size={18} />
                Start Learning
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {topics.slice(0, 4).map((topic, index) => (
                <TopicCard
                  key={topic.id}
                  topic={topic}
                  className={`stagger-${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Recent journals section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-display font-semibold text-primary-950">
              Recent Journals
            </h2>
            <Link
              to="/journals"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all
            </Link>
          </div>

          {recentJournals.length === 0 ? (
            <div className="bg-white rounded-2xl border p-6 text-center">
              <Calendar className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-slate-500">No journals yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentJournals.map((journal, index) => (
                <JournalPreview
                  key={journal.id}
                  journal={journal}
                  className={`stagger-${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

